/**
 * Phase 150 — Public Unsubscribe Page
 * Reached via the email footer link `?lead_id=xxx`.  Idempotent — always
 * shows the same success state regardless of whether the record existed.
 */
import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Loader2, Mail } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState('processing'); // processing | done | error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const lead_id = params.get('lead_id') || '';
    const email = params.get('email') || '';
    const phone = params.get('phone') || '';
    axios.post(`${API}/public/outreach/unsubscribe`, {
      lead_id, email, phone, source: 'email_footer_link',
    }).then((r) => {
      setStatus('done');
      setMessage(r.data?.message || "You've been unsubscribed.");
    }).catch(() => {
      // Even on error we show success to avoid information-leakage —
      // matches the backend's idempotent posture.
      setStatus('done');
      setMessage("You've been unsubscribed. You won't receive any more emails or WhatsApp messages from Out-Play.");
    });
  }, [params]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-emerald-50/40 p-4"
      data-testid="phase150-unsubscribe-page">
      <Card className="w-full max-w-md border-slate-200 shadow-xl shadow-slate-900/5">
        <CardContent className="p-7 sm:p-9 space-y-5 text-center">
          <div className="flex justify-center">
            <span className="inline-block font-display font-extrabold text-2xl tracking-tight">
              <span className="text-slate-800">Out</span><span className="text-red-500">-Play</span>
            </span>
          </div>
          {status === 'processing' && (
            <>
              <Loader2 className="h-10 w-10 text-emerald-600 animate-spin mx-auto" />
              <h1 className="text-lg font-semibold text-slate-800">Processing your request…</h1>
            </>
          )}
          {status === 'done' && (
            <>
              <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="h-7 w-7 text-emerald-600" />
              </div>
              <h1 className="text-lg font-bold text-slate-800" data-testid="phase150-unsubscribe-success-title">
                You're all set.
              </h1>
              <p className="text-sm text-slate-600 leading-relaxed" data-testid="phase150-unsubscribe-success-message">
                {message}
              </p>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-600 text-left flex gap-3">
                <Mail className="h-4 w-4 text-slate-500 mt-0.5 flex-shrink-0" />
                <div>
                  Sent in error or want to re-engage later? Email <a href="mailto:contact@out-play.in" className="text-emerald-700 font-semibold underline">contact@out-play.in</a> and we'll personally re-enroll you.
                </div>
              </div>
              <Link to="/">
                <Button variant="outline" className="w-full h-10 rounded-lg text-sm font-semibold" data-testid="phase150-unsubscribe-home-btn">
                  Back to out-play.in
                </Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
